import { useState, useEffect } from 'react';
import CompassService from '../services/CompassService';

/**
 * Global compass manager - singleton pattern to ensure only one listener exists
 */
const compassManager = {
  heading: null,
  rawEvent: null, // Raw DeviceOrientationEvent for advanced processing
  isSupported: false,
  permissionStatus: 'unknown',
  subscribers: new Set(),
  rawSubscribers: new Set(), // Subscribers that want raw event data
  orientationHandler: null,
  nativeListener: null,
  initialized: false,
  eventType: null, // Track which event type we're listening to ('deviceorientationabsolute' or 'deviceorientation')
  beforeUnloadHandler: null, // Single beforeunload handler for cleanup

  subscribe(callback) {
    this.subscribers.add(callback);
    // Immediately notify with current value
    callback(this.heading, this.isSupported, this.permissionStatus);
    return () => this.subscribers.delete(callback);
  },

  subscribeRaw(callback) {
    this.rawSubscribers.add(callback);
    // Immediately notify with current raw event if available
    if (this.rawEvent) {
      callback(this.rawEvent, this.isSupported, this.permissionStatus);
    }
    return () => this.rawSubscribers.delete(callback);
  },

  notify(heading, isSupported, permissionStatus, rawEvent = null) {
    this.heading = heading;
    this.isSupported = isSupported;
    this.permissionStatus = permissionStatus;
    if (rawEvent) {
      this.rawEvent = rawEvent;
    }
    this.subscribers.forEach(cb => cb(heading, isSupported, permissionStatus));
    if (rawEvent) {
      this.rawSubscribers.forEach(cb => cb(rawEvent, isSupported, permissionStatus));
    }
  },

  async initialize() {
    if (this.initialized) return;
    this.initialized = true;

    try {
      // Prefer native compass when available (Capacitor)
      // NOTE: Native compass doesn't provide raw events (alpha/beta/gamma), so we still need web API
      // for components that need advanced processing. Native compass is used as a fallback.
      if (CompassService.isNative()) {
        try {
          this.nativeListener = await CompassService.addListener((headingDeg) => {
            const normalized = ((headingDeg % 360) + 360) % 360;
            // Native compass doesn't provide raw events, so we only notify heading subscribers
            this.notify(normalized, true, 'granted', null);
          });
          // Continue to web API for raw event support (UnifiedMap needs alpha/beta/gamma)
        } catch (error) {
          console.warn('📍 Native compass failed, falling back to web API:', error);
          // Fall through to web API
        }
      }

      // Web Device Orientation API (needed for raw events even if native compass works)
      if (typeof window === 'undefined' || typeof DeviceOrientationEvent === 'undefined') {
        this.notify(null, false, 'unsupported');
        return;
      }

      // Check secure context (required for Device Orientation)
      if (!window.isSecureContext) {
        this.notify(null, false, 'insecure');
        return;
      }

      // Handle iOS permission request
      if (typeof DeviceOrientationEvent.requestPermission === 'function') {
        try {
          const permission = await DeviceOrientationEvent.requestPermission();
          if (permission === 'granted') {
            this.permissionStatus = 'granted';
            this.isSupported = true;
          } else {
            // Permission denied - notify but don't return, try to use existing listeners
            this.permissionStatus = 'denied';
            this.isSupported = false;
            this.notify(this.heading, false, 'denied');
            // Don't return - might still work if permission was already granted
          }
        } catch (error) {
          // Permission request failed (not in user gesture context)
          // Try anyway - might work if permission was already granted
          this.permissionStatus = 'unknown';
          this.isSupported = true;
        }
      } else {
        // No permission needed
        this.permissionStatus = 'granted';
        this.isSupported = true;
      }

      // Set up orientation event handler
      this.orientationHandler = (event) => {
        let headingValue = null;

        // iOS Safari: webkitCompassHeading is the most reliable
        if (event.webkitCompassHeading !== undefined && event.webkitCompassHeading !== null) {
          headingValue = event.webkitCompassHeading;
        } 
        // Android/other: use alpha (device orientation around Z-axis)
        else if (event.alpha !== null && event.alpha !== undefined && typeof event.alpha === 'number' && isFinite(event.alpha)) {
          // Normalize alpha to 0-360 range
          headingValue = ((event.alpha % 360) + 360) % 360;
        }

        if (headingValue !== null && !Number.isNaN(headingValue) && headingValue >= 0 && headingValue <= 360) {
          const normalized = ((headingValue % 360) + 360) % 360;
          // Notify with both processed heading and raw event for advanced processing
          this.notify(normalized, this.isSupported, this.permissionStatus, event);
        } else if (event.alpha !== null || event.webkitCompassHeading !== null) {
          // Even if heading is invalid, notify raw subscribers with the event
          this.notify(this.heading, this.isSupported, this.permissionStatus, event);
        }
      };

      // Try deviceorientationabsolute first (better for Android), fallback to deviceorientation
      if ('ondeviceorientationabsolute' in window) {
        this.eventType = 'deviceorientationabsolute';
        window.addEventListener('deviceorientationabsolute', this.orientationHandler, true);
      } else {
        this.eventType = 'deviceorientation';
        window.addEventListener('deviceorientation', this.orientationHandler, true);
      }

      // Set up page unload cleanup (only once)
      if (!this.beforeUnloadHandler) {
        this.beforeUnloadHandler = () => {
          this.cleanup();
        };
        window.addEventListener('beforeunload', this.beforeUnloadHandler);
      }
    } catch (error) {
      console.error('📍 Error initializing compass:', error);
      this.notify(null, false, 'error');
    }
  },

  // Cleanup method to remove listeners (called on page unload or when needed)
  cleanup() {
    if (this.nativeListener && this.nativeListener.remove) {
      try {
        this.nativeListener.remove();
      } catch (e) {
        console.warn('📍 Error removing native compass listener:', e);
      }
      this.nativeListener = null;
    }

    if (this.orientationHandler && this.eventType) {
      try {
        window.removeEventListener(this.eventType, this.orientationHandler, true);
      } catch (e) {
        console.warn('📍 Error removing orientation listener:', e);
      }
      this.orientationHandler = null;
      this.eventType = null;
    }

    if (this.beforeUnloadHandler) {
      try {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      } catch (e) {
        console.warn('📍 Error removing beforeunload handler:', e);
      }
      this.beforeUnloadHandler = null;
    }

    this.initialized = false;
  },

  // Re-initialize compass (useful for permission requests)
  async reinitialize() {
    this.cleanup();
    this.initialized = false;
    await this.initialize();
  }
};

/**
 * Shared compass hook - manages a single Device Orientation listener
 * that can be used by multiple components without duplication.
 * 
 * Returns the current compass heading (0-360 degrees, where 0° = North)
 * Works even when device is stationary (unlike geolocation heading).
 */
export function useCompass() {
  const [heading, setHeading] = useState(compassManager.heading);
  const [isSupported, setIsSupported] = useState(compassManager.isSupported);
  const [permissionStatus, setPermissionStatus] = useState(compassManager.permissionStatus);

  useEffect(() => {
    // Initialize compass if not already done
    compassManager.initialize();

    // Subscribe to compass updates
    const unsubscribe = compassManager.subscribe((newHeading, newIsSupported, newPermissionStatus) => {
      setHeading(newHeading);
      setIsSupported(newIsSupported);
      setPermissionStatus(newPermissionStatus);
    });

    // Cleanup is handled by compassManager singleton on page unload
    return unsubscribe;
  }, []);

  return { heading, isSupported, permissionStatus };
}

/**
 * Hook for components that need raw DeviceOrientationEvent data
 * (e.g., UnifiedMap with advanced calibration/processing)
 */
export function useCompassRaw() {
  const [rawEvent, setRawEvent] = useState(compassManager.rawEvent);
  const [isSupported, setIsSupported] = useState(compassManager.isSupported);
  const [permissionStatus, setPermissionStatus] = useState(compassManager.permissionStatus);

  useEffect(() => {
    // Initialize compass if not already done
    compassManager.initialize();

    // Subscribe to raw compass events
    const unsubscribe = compassManager.subscribeRaw((newRawEvent, newIsSupported, newPermissionStatus) => {
      setRawEvent(newRawEvent);
      setIsSupported(newIsSupported);
      setPermissionStatus(newPermissionStatus);
    });

    // Cleanup is handled by compassManager singleton on page unload
    return unsubscribe;
  }, []);

  return { rawEvent, isSupported, permissionStatus };
}

